"use client";

import React, { useState } from "react";
import { GlassCard } from "../components/ui/glass-card";
import { LimitedInput } from "../components/ui/limited-input";
import { FIELD_LIMITS } from "../utils/fieldLimits";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../components/ui/use-toast";
import api, { persistAuthSession } from "../services/api";
import { useHistory } from "react-router";
import { useKeyboardHeight } from "../hooks/useKeyboardHeight";

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [acceptTos, setAcceptTos] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const router = useHistory();
  const { toast } = useToast();
  const keyboardHeight = useKeyboardHeight();

  const handleOpenMultipleLinks = () => {
    window.open(
      "https://iodized-parent-254.notion.site/Informativa-Privacy-ECard-294516d8da30801cba4bcad5bf9155c1",
      "_blank",
    );
    window.open(
      "https://iodized-parent-254.notion.site/Cookie-Policy-ECard-294516d8da308093b59eda58a52f10c3",
      "_blank",
    );
  };

  const handleOpenTermsOfService = () => {
    window.open(
      "https://iodized-parent-254.notion.site/Termini-e-Condizioni-ECard-294516d8da308011b97ff73d07b4c62a",
      "_blank",
    );
  };

  const handleRegister = async () => {
    if (isLoading) return;

    if (!firstName.trim()) {
      toast({
        title: "Campo obbligatorio",
        description: "Inserisci il nome.",
        variant: "error",
      });
      return;
    }
    if (!lastName.trim()) {
      toast({
        title: "Campo obbligatorio",
        description: "Inserisci il cognome.",
        variant: "error",
      });
      return;
    }
    if (!email.trim()) {
      toast({
        title: "Campo obbligatorio",
        description: "Inserisci l'email.",
        variant: "error",
      });
      return;
    }
    if (!acceptTos) {
      toast({
        title: "Campo obbligatorio",
        description: "Devi accettare i Termini di Servizio.",
        variant: "error",
      });
      return;
    }
    if (!acceptPrivacy) {
      toast({
        title: "Campo obbligatorio",
        description: "Devi accettare la Privacy Policy e i Cookies.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await api.post("/register", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        accept_tos: acceptTos,
        accept_privacy: acceptPrivacy,
      });

      const token = res.data.access_token;
      if (!token) {
        throw new Error("Token mancante");
      }

      persistAuthSession(res.data);

      toast({
        title: "Account creato!",
        description:
          res.data.warning ||
          "Controlla la tua email per impostare la password personale.",
      });

      router.push("/loading");
    } catch (e: any) {
      toast({
        title: "Errore",
        description: e?.response?.data?.detail ?? "Registrazione fallita",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="overflow-y-auto"
      style={{
        height: `calc(100vh - ${keyboardHeight}px)`,
      }}
    >
      <div className="flex min-h-full items-center justify-center p-4 pb-6 relative">
        <div className="absolute top-1/5 left-1/5 w-48 h-48 rounded-full bg-teal-400/10 blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/5 right-1/5 w-72 h-72 rounded-full bg-teal-300/10 blur-3xl animate-pulse"></div>

        <GlassCard hover={false} className="max-w-md w-full p-8 space-y-6 z-10">
          <h1 className="text-center text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-200 to-teal-500">
            Registrati
          </h1>
          <p className="text-center text-sm text-white/70">
            In pochi secondi crei la tua Ecard. La password la imposti dopo via
            email.
          </p>

          <div className="space-y-2">
            <Label htmlFor="firstName">Nome *</Label>
            <LimitedInput
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Mario"
              maxLength={FIELD_LIMITS.register_first_name}
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Cognome *</Label>
            <LimitedInput
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Rossi"
              maxLength={FIELD_LIMITS.register_last_name}
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <LimitedInput
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mario@azienda.com"
              maxLength={FIELD_LIMITS.register_email}
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="tos"
                checked={acceptTos}
                onCheckedChange={(val: boolean) => setAcceptTos(!!val)}
              />
              <Label htmlFor="tos" className="text-sm">
                Accetto i{" "}
                <a
                  onClick={handleOpenTermsOfService}
                  className="text-teal-400 hover:underline cursor-pointer"
                >
                  Termini di Servizio
                </a>{" "}
                *
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="privacy"
                checked={acceptPrivacy}
                onCheckedChange={(val: boolean) => setAcceptPrivacy(!!val)}
              />
              <Label htmlFor="privacy" className="text-sm">
                Accetto la{" "}
                <a
                  onClick={handleOpenMultipleLinks}
                  className="text-teal-400 hover:underline cursor-pointer"
                >
                  Privacy Policy e Cookies
                </a>{" "}
                *
              </Label>
            </div>
          </div>

          <Button
            onClick={handleRegister}
            disabled={isLoading}
            className="w-full font-bold h-12 bg-gradient-to-r from-teal-200 to-teal-500 text-white shadow-lg hover:opacity-90"
          >
            {isLoading ? "Creazione in corso..." : "Crea la mia Ecard"}
          </Button>

          <p className="text-center text-sm">
            Hai già un account?{" "}
            <a href="/" className="text-teal-400 hover:underline">
              Accedi
            </a>
          </p>
        </GlassCard>
      </div>
    </div>
  );
};

export default Register;
